import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import { formatPrice, formatNumber, formatTimeAgo } from '../utils/formatters';

const MarketplacePage = () => {
  const navigate = useNavigate();
  const { domains } = useApp();
  const [filteredDomains, setFilteredDomains] = useState(domains);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    priceMin: 0,
    priceMax: 100000,
    tlds: [],
    categories: [],
    listingTypes: [],
    trending: false,
    isNew: false,
  });
  const [sortBy, setSortBy] = useState('recentlyListed');
  const [viewMode, setViewMode] = useState('grid'); // grid or list

  // Apply filters and search
  useEffect(() => {
    let result = [...domains];

    // Search filter
    if (searchQuery) {
      result = result.filter(d =>
        d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        d.category.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Price filter
    result = result.filter(
      d => d.price >= filters.priceMin && d.price <= filters.priceMax
    );

    // TLD filter
    if (filters.tlds.length > 0) {
      result = result.filter(d => filters.tlds.includes(d.tld));
    }

    // Category filter
    if (filters.categories.length > 0) {
      result = result.filter(d => filters.categories.includes(d.category));
    }

    // Listing type filter
    if (filters.listingTypes.length > 0) {
      result = result.filter(d =>
        filters.listingTypes.some(type => d.listingType.includes(type))
      );
    }

    // Trending/New
    if (filters.trending) {
      result = result.filter(d => d.trending);
    }
    if (filters.isNew) {
      result = result.filter(d => d.isNew);
    }

    // Sort
    switch (sortBy) {
      case 'priceLowHigh':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'priceHighLow':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'mostPopular':
        result.sort((a, b) => b.views - a.views);
        break;
      case 'trending':
        result.sort((a, b) => (b.trending ? 1 : 0) - (a.trending ? 1 : 0));
        break;
      default: // recentlyListed
        result.sort((a, b) => new Date(b.listedAt) - new Date(a.listedAt));
    }

    setFilteredDomains(result);
  }, [domains, filters, sortBy, searchQuery]);

  const removeFilter = (type, value) => {
    setFilters(prev => ({
      ...prev,
      [type]: prev[type].filter(v => v !== value)
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      priceMin: 0,
      priceMax: 100000,
      tlds: [],
      categories: [],
      listingTypes: [],
      trending: false,
      isNew: false,
    });
    setSearchQuery('');
  };

  const DomainCard = ({ domain }) => (
    <Card
      hover
      className={`cursor-pointer ${
        viewMode === 'list' ? 'p-4' : 'p-6'
      }`}
      onClick={() => navigate(`/domain/${domain.name}`)}
    >
      <div className={viewMode === 'list' ? 'flex items-center gap-6' : ''}>
        <div className="flex-1">
          <div className="flex items-start justify-between mb-2">
            <h3 className={`font-bold ${viewMode === 'list' ? 'text-lg' : 'text-xl'}`}>
              {domain.name}
            </h3>
            <div className="flex gap-1">
              {domain.trending && (
                <Badge variant="warning" size="sm">🔥</Badge>
              )}
              {domain.isNew && (
                <Badge variant="success" size="sm">New</Badge>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 mb-3">
            <Badge size="sm" variant="default">.{domain.tld}</Badge>
            <Badge size="sm" variant="info">{domain.category}</Badge>
          </div>

          {viewMode === 'grid' && (
            <>
              <p className="text-3xl font-bold text-blue-600 mb-4">
                {formatPrice(domain.price)}
              </p>
              <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                <span>👁 {formatNumber(domain.views)}</span>
                <span>❤️ {domain.likes}</span>
                <span>💬 {domain.comments}</span>
              </div>
              <div className="text-xs text-gray-500 mb-4">
                Listed {formatTimeAgo(domain.listedAt)} by @{domain.listedBy}
              </div>
              <Button fullWidth size="sm">
                View Details
              </Button>
            </>
          )}
        </div>

        {viewMode === 'list' && (
          <>
            <div className="text-right">
              <p className="text-2xl font-bold text-blue-600">
                {formatPrice(domain.price)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {formatTimeAgo(domain.listedAt)}
              </p>
            </div>
            <div className="text-sm text-gray-600">
              <div>👁 {formatNumber(domain.views)}</div>
              <div>❤️ {domain.likes}</div>
            </div>
            <Button size="sm">View</Button>
          </>
        )}
      </div>
    </Card>
  );

  const FilterSidebar = () => {
    const tldOptions = ['com', 'io', 'ai', 'net', 'org', 'app', 'co'];
    const categoryOptions = ['tech', 'crypto', 'finance', 'health', 'education', 'business', 'gaming'];
    const listingTypeOptions = [
      { id: 'buyNow', label: 'Buy Now' },
      { id: 'offers', label: 'Accepting Offers' },
      { id: 'trade', label: 'Open to Trades' }
    ];

    return (
      <div className="space-y-6">
        <Card className="p-6">
          <h3 className="font-bold mb-4">Price Range</h3>
          <div className="space-y-4">
            <input
              type="range"
              min="0"
              max="100000"
              step="1000"
              value={filters.priceMax}
              onChange={(e) =>
                setFilters({ ...filters, priceMax: parseInt(e.target.value) })
              }
              className="w-full"
            />
            <div className="flex gap-2">
              <input
                type="number"
                placeholder="Min"
                value={filters.priceMin}
                onChange={(e) =>
                  setFilters({ ...filters, priceMin: parseInt(e.target.value) || 0 })
                }
                className="w-full px-3 py-2 border rounded"
              />
              <input
                type="number"
                placeholder="Max"
                value={filters.priceMax}
                onChange={(e) =>
                  setFilters({ ...filters, priceMax: parseInt(e.target.value) || 100000 })
                }
                className="w-full px-3 py-2 border rounded"
              />
            </div>
            <p className="text-sm text-gray-600 text-center">
              ${filters.priceMin.toLocaleString()} - ${filters.priceMax.toLocaleString()}
            </p>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-bold mb-4">TLD</h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {tldOptions.map(tld => (
              <label key={tld} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.tlds.includes(tld)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setFilters({ ...filters, tlds: [...filters.tlds, tld] });
                    } else {
                      setFilters({
                        ...filters,
                        tlds: filters.tlds.filter(t => t !== tld)
                      });
                    }
                  }}
                  className="rounded"
                />
                <span className="capitalize">.{tld}</span>
              </label>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-bold mb-4">Category</h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {categoryOptions.map(category => (
              <label key={category} className="flex items-center gap-2 capitalize cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.categories.includes(category)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setFilters({
                        ...filters,
                        categories: [...filters.categories, category]
                      });
                    } else {
                      setFilters({
                        ...filters,
                        categories: filters.categories.filter(c => c !== category)
                      });
                    }
                  }}
                  className="rounded"
                />
                <span>{category}</span>
              </label>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-bold mb-4">Listing Type</h3>
          <div className="space-y-2">
            {listingTypeOptions.map(option => (
              <label key={option.id} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.listingTypes.includes(option.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setFilters({
                        ...filters,
                        listingTypes: [...filters.listingTypes, option.id]
                      });
                    } else {
                      setFilters({
                        ...filters,
                        listingTypes: filters.listingTypes.filter(t => t !== option.id)
                      });
                    }
                  }}
                  className="rounded"
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-bold mb-4">Activity</h3>
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.trending}
                onChange={(e) =>
                  setFilters({ ...filters, trending: e.target.checked })
                }
                className="rounded"
              />
              <span>🔥 Trending</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.isNew}
                onChange={(e) =>
                  setFilters({ ...filters, isNew: e.target.checked })
                }
                className="rounded"
              />
              <span>🆕 New (24h)</span>
            </label>
          </div>
        </Card>

        <Button fullWidth variant="outline" onClick={clearAllFilters}>
          Clear All Filters
        </Button>
      </div>
    );
  };

  const hasActiveFilters =
    filters.tlds.length > 0 ||
    filters.categories.length > 0 ||
    filters.listingTypes.length > 0 ||
    filters.trending ||
    filters.isNew ||
    filters.priceMin > 0 ||
    filters.priceMax < 100000;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Top bar */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-6">Domain Marketplace</h1>

          {/* Search and controls */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search domains by keyword, TLD, or category..."
                className="w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-3 rounded-lg border bg-white"
              >
                <option value="recentlyListed">Recently Listed</option>
                <option value="priceLowHigh">Price: Low to High</option>
                <option value="priceHighLow">Price: High to Low</option>
                <option value="mostPopular">Most Popular</option>
                <option value="trending">Trending</option>
              </select>

              <button
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                className="px-4 py-3 border rounded-lg hover:bg-gray-100 bg-white"
                title={`Switch to ${viewMode === 'grid' ? 'list' : 'grid'} view`}
              >
                {viewMode === 'grid' ? '☰' : '▦'}
              </button>
            </div>
          </div>
        </div>

        <div className="flex gap-8">
          {/* Filters sidebar */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <FilterSidebar />
          </div>

          {/* Main content */}
          <div className="flex-1">
            {/* Results count and active filters */}
            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                <strong>{filteredDomains.length}</strong> domain{filteredDomains.length !== 1 ? 's' : ''} found
              </p>

              {/* Active filter chips */}
              {hasActiveFilters && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {filters.tlds.map(tld => (
                    <Badge key={tld} variant="info">
                      .{tld}
                      <button
                        onClick={() => removeFilter('tlds', tld)}
                        className="ml-2 hover:text-red-600"
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                  {filters.categories.map(category => (
                    <Badge key={category} variant="info">
                      {category}
                      <button
                        onClick={() => removeFilter('categories', category)}
                        className="ml-2 hover:text-red-600"
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                  {filters.trending && (
                    <Badge variant="warning">
                      🔥 Trending
                      <button
                        onClick={() => setFilters({ ...filters, trending: false })}
                        className="ml-2 hover:text-red-600"
                      >
                        ×
                      </button>
                    </Badge>
                  )}
                  {filters.isNew && (
                    <Badge variant="success">
                      🆕 New
                      <button
                        onClick={() => setFilters({ ...filters, isNew: false })}
                        className="ml-2 hover:text-red-600"
                      >
                        ×
                      </button>
                    </Badge>
                  )}
                  <button
                    onClick={clearAllFilters}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>

            {/* Domain grid/list */}
            {filteredDomains.length > 0 ? (
              <div
                className={
                  viewMode === 'grid'
                    ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6'
                    : 'space-y-4'
                }
              >
                {filteredDomains.map(domain => (
                  <DomainCard key={domain.id} domain={domain} />
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <div className="text-6xl mb-4">🔍</div>
                <p className="text-xl text-gray-600 mb-4">
                  No domains match your filters
                </p>
                <Button onClick={clearAllFilters}>
                  Clear All Filters
                </Button>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketplacePage;
